import { useState, type ReactNode } from 'react';
import { ArrowLeft, BadgeCheck, ImagePlus, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTitle
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { toAddressLines } from '@/util/formatting';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { DESKTOP_BREAKPOINT_QUERY } from '@/config/ui.constants';
import type { ImageAssetType, ServiceType } from '@/components/ui/carousel';
import CardFormModal from '@/components/orders/payment/components/CardFormModal';
import { useAssetServiceOrder } from './useAssetServiceOrder';
import { useAssetOrderPayment } from './useAssetOrderPayment';
import { useAssetOrderPromo } from './useAssetOrderPromo';
import OrderServicePhotoPane from './OrderServicePhotoPane';
import OrderServiceActions from './OrderServiceActions';
import OrderServiceSummary, {
  priceText,
  type OrderStep
} from './OrderServiceSummary';

type Props = {
  open: boolean;
  service: ServiceType;
  photos: ImageAssetType[];
  propertyAddress: string;
  onClose: () => void;
  edit?: { lineItemUUID?: string; lineAssetId: string; initialNote: string; originalRefIds: string[] };
  /**
   * Sub-lg sheet only (QA round 5 follow-up, Product request): renders a back
   * arrow left of the sheet header that returns to the surface this order
   * came from (the selection bar's Add Services sheet). Pass only when there
   * IS such a surface — the desktop dialog and the viewer's order-edit path
   * never show it.
   */
  onBack?: () => void;
  /**
   * Fires once the sheet's slide-down exit finishes after `open` flips false
   * while the component stays mounted — the host unmounts on it, so the back
   * transition (this sheet down, services sheet up) can actually play. The
   * plain onClose path still unmounts immediately, as before.
   */
  onExited?: () => void;
};

function ConfirmRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between gap-6 border-b border-border py-2 last:border-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium">{value}</dd>
    </div>
  );
}

export default function OrderServiceModal({
  open,
  service,
  photos,
  propertyAddress,
  onClose,
  edit,
  onBack,
  onExited
}: Props) {
  const isDesktop = useMediaQuery(DESKTOP_BREAKPOINT_QUERY);
  const order = useAssetServiceOrder({
    photos,
    service,
    propertyAddress,
    onClose,
    edit
  });
  const pay = useAssetOrderPayment();
  const promo = useAssetOrderPromo(order.subtotal);
  const effectiveTotal = promo.isApplied
    ? promo.discountedTotal
    : order.subtotal;
  const effectiveFree = order.isFree || effectiveTotal === 0;
  const [step, setStep] = useState<OrderStep>('main');
  const [orderNumber, setOrderNumber] = useState<string | undefined>();
  const [payView, setPayView] = useState<'current' | 'list'>('current');
  const [showAddCard, setShowAddCard] = useState(false);

  const handleSubmit = async () => {
    const ok = await order.submit();
    if (!ok) return;
    // Close immediately on success — the hook already dispatches a success toast. A delayed
    // in-modal flash let the cart re-fetch flip the modal into edit mode ("Remove from Cart" /
    // "Updated ✓") before it closed, which read as sluggish.
    onClose();
  };

  const confirmPay = async () => {
    setStep('placing');
    try {
      const num = await pay.placeOrderNow({
        photos: order.items,
        service,
        note: order.note,
        propertyAddress,
        total: effectiveTotal,
        promoCodeId: promo.promoCodeId
      });
      setOrderNumber(num);
      if (order.isEdit) await order.removeLine({ silent: true });
      setStep('done');
    } catch {
      setStep('paying'); // error toast already dispatched by the hook
    }
  };

  const showCardUi = !effectiveFree && !pay.isSkipCheckout;

  // While the add/update is in flight, show an in-progress label + spinner instead of closing
  // instantly (an instant close read as jarring); the modal closes on success (handleSubmit).
  const submitInProgressLabel = order.isEdit ? 'Updating…' : 'Adding to Cart…';
  const submitButtonLabel = order.submitting
    ? submitInProgressLabel
    : order.submitLabel;

  if (step === 'done') {
    // The confirmation is a small centered card at every width — nothing in
    // it needs the bottom-sheet treatment, and a fullscreen-ish sheet for
    // four summary rows would read as oversized.
    return (
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-[520px]">
          <div className="flex items-center gap-2">
            <ImagePlus className="h-6 w-6 text-foreground" aria-hidden />
            <DialogTitle>
              <Typography variant="heading-lg">Order Service</Typography>
            </DialogTitle>
          </div>
          <div className="flex flex-col items-center gap-3 py-4">
            <div
              className="flex h-16 w-16 items-center justify-center rounded-full bg-success-soft text-success"
              aria-hidden="true"
            >
              <BadgeCheck className="h-9 w-9" />
            </div>
            <Typography variant="heading-lg">Order Confirmed</Typography>
            <dl className="w-full text-sm">
              {orderNumber && <ConfirmRow label="Order" value={orderNumber} />}
              <ConfirmRow
                label="Address"
                value={toAddressLines(propertyAddress).map((line) => (
                  <span key={line} className="block">
                    {line}
                  </span>
                ))}
              />
              <ConfirmRow label="Service" value={service.name} />
              <ConfirmRow
                label="Quantity"
                value={`${order.count} ${
                  order.count === 1 ? 'photo' : 'photos'
                }`}
              />
            </dl>
          </div>
          <div className="flex justify-end">
            <Button onClick={onClose}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const photoPane = (
    <OrderServicePhotoPane
      serviceName={service.name}
      unitPriceText={priceText(order.unitPrice)}
      addressLine={propertyAddress}
      photos={order.items.map((photo) => ({
        id: photo.assetUUID,
        thumbnailUrl: photo.caches?.['300w'] || photo.caches?.['830w'] || '',
        caption: photo.caption,
        lineLabel: priceText(order.unitPrice)
      }))}
      note={order.note}
      onNoteChange={order.setNote}
      onRemovePhoto={order.removePhoto}
      noteRequired={!order.isAiStaging}
      // Desktop dialog keeps its internally-scrolling pane; the sheet flows so
      // the whole sheet scrolls as ONE column (2026-08-21).
      constrainHeight={isDesktop}
    />
  );

  // One prop bag, two presentations (see OrderServicePaneProps): the summary
  // content scrolls, the actions pin in the sheet's footer.
  const paneProps = {
    service,
    order,
    pay,
    promo,
    step,
    setStep,
    payView,
    setPayView,
    effectiveTotal,
    effectiveFree,
    showCardUi,
    submitButtonLabel,
    onSubmit: handleSubmit,
    onConfirmPay: confirmPay,
    onAddCard: () => setShowAddCard(true),
    onClose
  };
  const summary = <OrderServiceSummary {...paneProps} />;
  const actions = <OrderServiceActions {...paneProps} />;

  return (
    <>
      {isDesktop ? (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
          {/* Override the base max-h-[90vh]: 90vh balloons an image-heavy order
              on tall (1440p/4K) screens. min() keeps 90vh on short windows and
              imposes an absolute pixel ceiling on taller screens (tuned by eye).
              The photo list scrolls within. */}
          <DialogContent
            className="max-h-[min(90vh,650px)] max-w-[860px] p-0 gap-0 overflow-hidden"
            onPointerDownOutside={(e) => e.preventDefault()}
          >
            {/* DialogContent renders its own close (X) button top-right. */}
            {/* DialogContent is a flex column capped at max-h-[90vh] (base); the
                header stays fixed and the body row takes the remaining height so
                its columns can scroll internally instead of overflowing. */}
            <div className="flex shrink-0 items-center gap-2 border-b border-border px-6 py-4">
              <ImagePlus className="h-6 w-6 text-foreground" aria-hidden />
              <DialogTitle>
                <Typography variant="heading-lg">Order Service</Typography>
              </DialogTitle>
            </div>

            {/* grid-rows-1 (a single 1fr row) makes both columns fill the bounded
                body height so their inner scroll regions engage; min-h-0 lets
                them shrink below content height. */}
            <div className="grid min-h-0 flex-1 grid-cols-[1fr_300px] grid-rows-1">
              {/* Left: photos + shared note */}
              {photoPane}

              {/* Right: summary + actions */}
              <aside className="min-h-0 overflow-y-auto border-l border-border p-6">
                {summary}
                {actions}
              </aside>
            </div>
          </DialogContent>
        </Dialog>
      ) : (
        // Sub-lg: bottom sheet (design 2026-08-19 round 4, spec §1.11), same
        // content stacked — photo pane scrolls in the body, summary + CTAs in
        // a bordered footer. Steffon's call: the photo pane STAYS on mobile
        // (the design sheet's missing photo list is placeholder
        // simplification, not intent).
        <Drawer open={open} onOpenChange={(o) => !o && onClose()}>
          <DrawerContent
            data-testid="order-service-sheet"
            className="max-h-[94dvh]"
            // Match the desktop dialog's protection for typed note text: an
            // accidental tap outside must not discard the sheet. Drag-down
            // and the X remain deliberate close gestures.
            onPointerDownOutside={(e) => e.preventDefault()}
            // Exit-complete signal for the back transition: when the host
            // flips `open` false while keeping this mounted, vaul runs its
            // 0.5s transform transition on this element and Radix holds the
            // DOM until it ends — this is the moment the host can unmount.
            // NOT vaul's Root onAnimationEnd: that only fires for closes
            // made through vaul's own setter (drag/X), never for a
            // controlled prop flip (verified against vaul 1.1.2). The
            // `open` guard drops stragglers from an exit that a re-pick
            // interrupted, and the entry transition's end.
            onTransitionEnd={(e) => {
              if (
                !open &&
                e.target === e.currentTarget &&
                e.propertyName === 'transform'
              ) {
                onExited?.();
              }
            }}
          >
            <div className="flex shrink-0 items-center gap-2 border-b border-border px-4 py-3">
              {/* Back to the Add Services sheet (Product request, QA round 5
                  follow-up) — this sheet slides down while that one slides
                  back up, mirroring the pick transition that got here. Same
                  plain-button chrome as the DrawerClose X for symmetry. */}
              {onBack && (
                <button
                  type="button"
                  aria-label="Back to Add Services"
                  onClick={onBack}
                  className="-ml-2 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
              )}
              <ImagePlus className="h-5 w-5 text-foreground" aria-hidden />
              <DrawerTitle asChild>
                <Typography as="h2" variant="heading-md">
                  Order Service
                </Typography>
              </DrawerTitle>
              <DrawerClose
                aria-label="Close"
                className="ml-auto inline-flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </DrawerClose>
            </div>
            {/* ONE scrolling column (2026-08-21 on-device fix). This used to be
                two panes — a flex-1 photo pane over a max-h-[45dvh] scrolling
                footer — and on a phone the paying step's taller summary
                squeezed the photo list toward zero, so the photos disappeared
                and Back sat below the fold of a nested scroller. Now photos,
                note and summary flow together and the sheet scrolls once. */}
            <div className="min-h-0 flex-1 overflow-y-auto">
              {photoPane}
              <div className="border-t border-border p-4">{summary}</div>
            </div>
            {/* Actions pin: Confirm & Pay stays reachable without scrolling,
                and the bottom inset keeps it clear of the iOS home indicator
                and Safari's bottom toolbar. Renders nothing while the card
                list is open, so the bar collapses rather than sitting empty. */}
            <div className="shrink-0 border-t border-border px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] empty:hidden">
              {actions}
            </div>
          </DrawerContent>
        </Drawer>
      )}

      {showAddCard && (
        <CardFormModal
          open={showAddCard}
          onOpenChange={setShowAddCard}
          isUpdate={false}
          isSingleCard={pay.cards.length <= 1}
          onSuccess={() => {
            setShowAddCard(false);
            pay.refetchCards();
          }}
        />
      )}
    </>
  );
}
